// src/components/blocks/Bumpout.jsx
import React, { useContext, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { DeckContext } from '../../context/DeckContextV3';
import BaseBlock from './BaseBlockV3';
import EdgeIndicator from './edgeIndicatorV3/EdgeIndicatorV3';

import { formatFeetInches } from '../../utils/measurementUtils';
import { BumpoutBlockProps } from '../../../../../../types/shared/propsV3';
import EDGE_WIDTH from '../../../../../../constants/edgeWidth';

const Bumpout: React.FC<BumpoutBlockProps> = ({
	label,
	bumpout,
	onSelect,
	onUpdateBlock,
	setIsDragging,
}) => {
	const { state, dispatch } = useContext(DeckContext);
	const nodeRef = useRef(null); // Create a ref for the DOM node
	const { scaleFactor } = state;

	return (
		<BaseBlock
			block={bumpout}
			onSelect={onSelect}
			onUpdateBlock={onUpdateBlock}
			setIsDragging={setIsDragging}
			disabled={false} // Always draggable within DeckBox
			nodeRef={nodeRef} // Pass nodeRef to BaseBlock
		>
			<Box
				ref={nodeRef} // Attach nodeRef to the DOM node
				style={{ transform: `rotate(${bumpout.rotation || 0}deg)` }}
				sx={{
					width: bumpout.displayDimensions.width * scaleFactor, // e.g., 80px
					height: bumpout.displayDimensions.height * scaleFactor, // e.g., 20px
					bgcolor: '#03A9F4', // Blue background
					cursor: 'pointer',
					position: 'absolute',
					zIndex: '10',
					border: '0.5px solid rgba(255, 255, 255, 0.1)',
				}}
				onClick={(e) => {
					e.stopPropagation();
					onSelect(bumpout);
				}}
				onTouchStart={(e) => {
					e.stopPropagation();
				}}
				onTouchEnd={(e) => {
					e.stopPropagation();
					e.preventDefault();
					onSelect(bumpout);
				}}
			>
				<Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
					{/* Edge Indicators */}
					{bumpout.edges.map((edge) => (
						<EdgeIndicator
							key={edge.label}
							edge={edge}
							onSelectEdge={(label) => {
								dispatch({
									type: 'SELECT_BLOCK',
									payload: bumpout,
								});

								dispatch({
									type: 'SELECT_EDGE',
									payload: { blockId: bumpout.id, edgeLabel: label },
								});
							}}
							isSelected={
								state.selectedEdge?.blockId == bumpout.id &&
								state.selectedEdge?.edgeLabel === edge.label
							}
							parentIsSelected={state.selectedBlock?.id === bumpout.id}
						/>
					))}

					<Box
						sx={{
							width: '100%',
							display: 'flex',
							justifyContent: 'center',
							paddingTop: `${EDGE_WIDTH}px`,
							cursor: 'pointer',
						}}
						onClick={(e) => {
							e.stopPropagation();
							onSelect(bumpout);
						}}
						onTouchStart={(e) => {
							e.stopPropagation();
						}}
						onTouchEnd={(e) => {
							e.stopPropagation();
							e.preventDefault();
							onSelect(bumpout);
						}}
					>
						<Typography
							variant="caption"
							position="absolute"
							lineHeight={1.15}
							color="rgba(0, 0, 0, 0.6)"
							noWrap
							zIndex={10}
						>
							<p>
								<b>{label}</b>
							</p>
							{formatFeetInches(bumpout.dimensions.depth)}
							<span className="px-0.5">&times;</span>
							{formatFeetInches(bumpout.dimensions.width)}
						</Typography>
					</Box>
				</Box>
			</Box>
		</BaseBlock>
	);
};

export default Bumpout;
